/**
 * AI CONTEXT
 * Purpose: state aggregator for TeacherView (session selection, summaries, date, impersonation-aware filtering).
 * Inputs: AdminViewContext + UserContext.effectiveUserId.
 * Exposes: selectedSession, session summaries, students/classes maps.
 * Used by: TeacherSchedule, TeacherAttendance, TeacherClassInfo.
 * Refactor: split selection logic and summary derivations into dedicated hooks.
 */
import { keyBy } from "lodash"
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { useAdminViewContext } from "#contexts/AdminViewContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { Dayjs } from "#customTypes/types"
import { AdminViewClassSession } from "#hooks/newHooks/useAdminViewData"
import { StudentsByExceptionType } from "#hooks/newHooks/useExceptionListData"
import {
  AttendanceSummary,
  getSessionAttendanceSummary,
  summarizeSessionStudentsByType,
} from "#utils/classHelpers"
import { getNow } from "#utils/dateHelpers"

interface TeacherViewContextType {
  sessionAttendanceSummaries: AttendanceSummary[]
  allSessionAttendanceSummary: AttendanceSummary

  sessionStudentTypeSummaries: {
    students: 0
    makeups: 0
    tryouts: 0
    plannedAbsences: 0
  }[]
  allSessionStudentTypeSummaries: {
    students: 0
    makeups: 0
    tryouts: 0
    plannedAbsences: 0
  }

  sessionsForCurrentUser: AdminViewClassSession[]
  selectedSession: AdminViewClassSession | null
  setSelectedSessionId: (sessionId: Uuid) => void

  studentsById: Record<Uuid, StudentConstructor>
  studentsByExceptionType: StudentsByExceptionType
  classesById: Record<Uuid, ClassConstructor>

  selectedDate: Dayjs
  setSelectedDate: (date: Dayjs) => void
}

export const TeacherViewContext = createContext<TeacherViewContextType | null>(
  null
)

const findSessionClosestToNow = (
  sessions: AdminViewClassSession[],
  now: Dayjs
): AdminViewClassSession => {
  // First, check if we're currently in a session
  const currentSession = sessions.find(
    (session) => now.isAfter(session.startTime) && now.isBefore(session.endTime)
  )

  if (currentSession) {
    return currentSession
  }

  // If not in a session, find the next session in the future
  const futureSessions = sessions
    .filter((session) => now.isBefore(session.startTime))
    .sort((a, b) => a.startTime.diff(b.startTime))

  if (futureSessions.length > 0) {
    return futureSessions[0] // Return the next upcoming session
  }

  // If no future sessions, find the closest past session
  let closestSession = sessions[0]
  let minDifference = Infinity

  for (const session of sessions) {
    const difference = Math.abs(now.diff(session.endTime))

    if (difference < minDifference) {
      minDifference = difference
      closestSession = session
    }
  }

  return closestSession
}

const TeacherViewContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const sessionIdRef = useRef<Uuid[]>([])
  // Use effectiveUserId to support impersonation - admin sees impersonated teacher's sessions
  const { effectiveUserId } = useUserContext()
  const [selectedSessionId, setSelectedSessionId] = useState<Uuid | null>(null)

  const {
    allSessions,
    selectedSessions: sessionsForCurrentUser,
    classesFromDb,
    isReady,
    selectedDate,
    setSelectedDate,
    setSelectedSessionIds,
    studentsById,
    studentsByExceptionType,
  } = useAdminViewContext()

  // Select the sessions for the effective user (supports impersonation)
  useEffect(() => {
    if (!effectiveUserId || !allSessions || !isReady) return

    const sessionIdsForCurrentUser = allSessions
      .filter((session) => {
        return session?.session?.teacherIds?.includes(effectiveUserId)
      })
      .map((session) => session.id)

    // This already memoizes the ids
    setSelectedSessionIds(sessionIdsForCurrentUser)

    sessionIdRef.current = sessionIdsForCurrentUser
  }, [
    allSessions,
    effectiveUserId,
    isReady,
    selectedSessionId,
    sessionsForCurrentUser,
    setSelectedSessionIds,
  ])

  useEffect(() => {
    const selectedSession = sessionsForCurrentUser.find(
      ({ session }) => session.id === selectedSessionId
    )

    if (
      effectiveUserId &&
      !selectedSession?.session.teacherIds?.includes(effectiveUserId)
    ) {
      const session = findSessionClosestToNow(sessionsForCurrentUser, getNow())
      setSelectedSessionId(session?.id ?? null)
    }
  }, [selectedSessionId, sessionsForCurrentUser, effectiveUserId])

  const classesById = useMemo(() => {
    return keyBy(classesFromDb, "id")
  }, [classesFromDb])

  const selectedSession = useMemo(() => {
    return (
      sessionsForCurrentUser.find(
        ({ session }) => session.id === selectedSessionId
      ) ?? null
    )
  }, [sessionsForCurrentUser, selectedSessionId])

  const sessionStudentTypeSummaries = useMemo(() => {
    if (!sessionsForCurrentUser || !studentsById || !studentsByExceptionType)
      return []

    return sessionsForCurrentUser.map(({ session }) => {
      return summarizeSessionStudentsByType(
        session,
        session.studentIds,
        studentsByExceptionType
      )
    })
  }, [sessionsForCurrentUser, studentsById, studentsByExceptionType])

  const sessionAttendanceSummaries = useMemo(() => {
    if (!sessionsForCurrentUser || !studentsById || !studentsByExceptionType)
      return []

    return sessionsForCurrentUser.map(({ session }) => {
      return getSessionAttendanceSummary(
        session,
        studentsById,
        studentsByExceptionType
      )
    })
  }, [sessionsForCurrentUser, studentsById, studentsByExceptionType])

  const allSessionStudentTypeSummaries = useMemo(() => {
    if (!sessionStudentTypeSummaries) return []

    return sessionStudentTypeSummaries?.reduce(
      (summary, sessionSummary) => {
        return {
          students: summary.students + sessionSummary.students,
          makeups: summary.makeups + sessionSummary.makeups,
          tryouts: summary.tryouts + sessionSummary.tryouts,
          plannedAbsences:
            summary.plannedAbsences + sessionSummary.plannedAbsences,
        }
      },
      {
        students: 0,
        makeups: 0,
        tryouts: 0,
        plannedAbsences: 0,
      }
    )
  }, [sessionStudentTypeSummaries])

  const allSessionAttendanceSummary = useMemo(() => {
    if (!sessionAttendanceSummaries)
      return {
        PRESENT: 0,
        TARDY: 0,
        ABSENT: 0,
        ENROLLED: 0,
        TOTAL: 0,
      }

    return sessionAttendanceSummaries.reduce(
      (summary, sessionSummary) => {
        return {
          PRESENT: summary.PRESENT + sessionSummary.PRESENT,
          TARDY: summary.TARDY + sessionSummary.TARDY,
          ABSENT: summary.ABSENT + sessionSummary.ABSENT,
          ENROLLED: summary.ENROLLED + sessionSummary.ENROLLED,
          TOTAL: summary.TOTAL + sessionSummary.TOTAL,
        }
      },
      {
        PRESENT: 0,
        TARDY: 0,
        ABSENT: 0,
        ENROLLED: 0,
        TOTAL: 0,
      }
    )
  }, [sessionAttendanceSummaries])

  return (
    <TeacherViewContext.Provider
      value={{
        sessionAttendanceSummaries,
        allSessionAttendanceSummary,
        sessionStudentTypeSummaries,
        allSessionStudentTypeSummaries,
        sessionsForCurrentUser,
        selectedSession,
        studentsById,
        classesById,
        studentsByExceptionType,
        selectedDate,
        setSelectedDate,
        setSelectedSessionId,
      }}
    >
      {children}
    </TeacherViewContext.Provider>
  )
}

export const useTeacherViewContext = () => {
  const context = useContext(TeacherViewContext)
  if (!context) {
    throw new Error(
      "useTeacherViewContext must be used within a TeacherViewContextProvider"
    )
  }
  return context
}

export default TeacherViewContextProvider
